import React from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import {
  CLASSIFICATION_STYLES,
  MULISH,
  OWNER_LABELS,
  STEP_ORDER,
} from "./vpnDebugConstants";
import {
  VpnDebugClassification,
  VpnDebugStep,
  VpnDebugStepId,
} from "./vpnDebugInterfaces";

interface Props {
  running: boolean;
  stage: string;
  steps: Partial<Record<VpnDebugStepId, VpnDebugStep>>;
  pendingSteps: VpnDebugStepId[];
  classification: VpnDebugClassification | null;
}

const Chip = ({
  label,
  color,
  border,
}: {
  label: string;
  color: string;
  border: string;
}) => (
  <Box
    component="span"
    sx={{
      ...MULISH,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.3px",
      color,
      border: `1px solid ${border}`,
      backgroundColor: "rgba(255,255,255,0.65)",
      borderRadius: "999px",
      px: 1,
      py: "2px",
    }}
  >
    {label}
  </Box>
);

const eyebrowSx = {
  ...MULISH,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.8px",
  color: "#6b7280",
};

/**
 * A run lands in two parts. Until BOTH are in, any classification held is the
 * tunnel plane's own verdict over half a ladder — which reads "Inconclusive, 4
 * checks skipped" and then flips to "Healthy" a minute later. Showing that as a
 * FINAL CLASSIFICATION is worse than showing nothing, so the banner stays a
 * progress indicator until `running` clears.
 */
export const VpnDebugClassificationBanner = ({
  running,
  stage,
  steps,
  pendingSteps,
  classification,
}: Props) => {
  if (running) {
    const done = STEP_ORDER.filter(
      (id) => steps[id] && !pendingSteps.includes(id)
    ).length;

    return (
      <Box
        sx={{
          backgroundColor: "#eaf1fb",
          border: "1px solid #cddcf3",
          borderLeft: "5px solid #283e6d",
          borderRadius: "8px",
          p: 2,
          mb: 2.5,
        }}
      >
        <Typography sx={eyebrowSx}>DIAGNOSTICS RUNNING</Typography>
        <Typography
          sx={{
            ...MULISH,
            fontSize: 19,
            fontWeight: 700,
            color: "#001943",
            mt: 0.25,
          }}
        >
          {stage || "Working…"}
        </Typography>
        <Typography sx={{ ...MULISH, fontSize: 13, color: "#283e6d", mt: 0.5 }}>
          {done} of {STEP_ORDER.length} checks complete.
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(done / STEP_ORDER.length) * 100}
          sx={{
            mt: 1.25,
            height: 5,
            borderRadius: 3,
            backgroundColor: "#cddcf3",
            "& .MuiLinearProgress-bar": { backgroundColor: "#283e6d" },
          }}
        />
      </Box>
    );
  }

  if (!classification) return null;

  const style =
    CLASSIFICATION_STYLES[classification.code] ||
    CLASSIFICATION_STYLES.inconclusive;
  const ownerLabel = OWNER_LABELS[classification.owner];

  return (
    <Box
      sx={{
        backgroundColor: style.backgroundColor,
        border: `1px solid ${style.accent}33`,
        borderLeft: `5px solid ${style.accent}`,
        borderRadius: "8px",
        p: 2,
        mb: 2.5,
      }}
    >
      <Typography sx={eyebrowSx}>FINAL CLASSIFICATION</Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 1,
          mt: 0.25,
        }}
      >
        <Typography
          sx={{ ...MULISH, fontSize: 22, fontWeight: 700, color: "#001943" }}
        >
          {classification.label}
        </Typography>
        {ownerLabel && (
          <Chip label={ownerLabel} color={style.accent} border={style.accent} />
        )}
        {classification.confidence && (
          <Chip
            label={`confidence ${classification.confidence}`}
            color="#5f6368"
            border="#c9ced6"
          />
        )}
      </Box>
      {classification.summary && (
        <Typography
          sx={{
            ...MULISH,
            fontSize: 14,
            color: "#283e6d",
            mt: 0.75,
            lineHeight: 1.5,
          }}
        >
          {classification.summary}
        </Typography>
      )}
      {classification.next_actions.length > 0 && (
        <Box sx={{ mt: 1.25 }}>
          <Typography sx={eyebrowSx}>
            {classification.next_actions.length > 1
              ? "NEXT ACTIONS"
              : "NEXT ACTION"}
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {classification.next_actions.map((action) => (
              <Box
                component="li"
                key={action}
                sx={{
                  ...MULISH,
                  fontSize: 13,
                  color: "#283e6d",
                  lineHeight: 1.55,
                }}
              >
                {action}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
